<template>
  <div class="indoor-building-selector">
    <b-form-select
      v-model="modelValue"
      v-bind:options="options"
      v-bind:size="size"
      v-bind:state="state"
      v-bind:disabled="disabled"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

/** すべての建物ID */
const ALL_INDOOR_BUILDING_ID = -1

/**
 * 建物選択コンポーネント
 */
export default {
  name: 'AppIndoorBuildingSelector',
  props: {
    /**
     * すべてのを選択している時に表示されるラベル(必須プロパティ)
     */
    selectedAllLabel: {
      type: String,
      required: true,
    },
    /**
     * 選択している建物ID
     */
    value: {
      type: Number,
      default: ALL_INDOOR_BUILDING_ID,
    },
    /**
     * 現場ID
     */
    indoorSiteId: {
      type: Number,
      default: null,
    },
    /**
     * サイズ
     * @values sm, md, lg
     */
    size: {
      type: String,
      default: 'sm',
    },
    /**
     * バリデーション状態
     */
    state: {
      type: Boolean,
      default: null,
    },
    /**
     * 非活性の場合はtrue
     */
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      modelValue: this.value,
    }
  },
  computed: {
    ...mapGetters('indoorBuilding', {
      /**
       * 建物データ
       * @type {Array.<Object>}
       */
      indoorBuildings: 'indoorBuildings',
    }),
    /**
     * 現場に紐づく建物
     * @type {Array.<Object>}
     */
    indoorBuildingsFiltered: function () {
      if (!this.indoorSiteId) {
        return this.indoorBuildings
      }
      return this.indoorBuildings.filter((item) =>
        item.indoor_sites.some((site) => site.id === this.indoorSiteId)
      )
    },
    /**
     * 選択値
     * @type {Array.<Object>}
     */
    options: function () {
      return [
        { value: ALL_INDOOR_BUILDING_ID, text: this.selectedAllLabel },
        ...this.indoorBuildingsFiltered.map((item) => ({
          value: item.id,
          text: item.name,
        })),
      ]
    },
    /**
     * 公開値
     * @type {Number}
     */
    publicValue: function () {
      return this.modelValue === ALL_INDOOR_BUILDING_ID ? null : this.modelValue
    },
  },
  created: function () {
    this.$_getIndoorBuildings()
  },
  watch: {
    value: function () {
      this.modelValue = this.value
    },
    publicValue: function () {
      this.$_emitChangedEvent()
    },
    indoorBuildingsFiltered: function () {
      // 建物未取得時は何もしない
      if (this.indoorBuildings.length === 0) return
      this.$_emitOptionsChangedEvent()

      // modelValueが選択肢になければmodelValueを初期値にする
      const isValueInOptions = this.options.some(
        (item) => item.value === this.modelValue
      )
      if (this.modelValue && !isValueInOptions) {
        this.$log.info('Clear selected value due to change options.')
        this.modelValue = ALL_INDOOR_BUILDING_ID
      }
    },
  },
  methods: {
    ...mapActions('indoorBuilding', {
      /**
       * 建物更新
       */
      $_updateIndoorBuildings: 'updateIndoorBuildingsWithThrottle',
    }),

    /**
     * 建物データ取得
     */
    $_getIndoorBuildings: async function () {
      try {
        await this.$_updateIndoorBuildings()
      } catch (error) {
        this.handleApiError(error)
      }
    },

    /**
     * 変更イベントを発行する
     */
    $_emitChangedEvent: function () {
      /**
       * 建物選択変更イベント
       *
       * @param {Number} id 建物ID
       */
      this.$emit('indoor-building-changed', this.publicValue)
    },

    /**
     * 選択肢の変更イベントを発行する
     */
    $_emitOptionsChangedEvent: function () {
      /**
       * 建物選択肢変更イベント
       *
       * @param {Array.<Object>} buidlings 建物
       */
      this.$emit(
        'indoor-building-options-changed',
        this.indoorBuildingsFiltered
      )
    },
  },
}
</script>
